import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { SettingsPageTemplate, SettingsCard, InfoTip } from '../components/SettingsPageTemplate'
import { PasskeysCard, TwoFactorCard, SMS2FACard } from '../components/SecurityFeaturesCards'
import { useTranslation } from 'react-i18next'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'

interface UserProfile {
  id: number
  username: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  profile_picture_url?: string
  profile_picture_settings?: any
  profile_picture_crop_settings?: any
  custom_form_slug?: string
  display_first_name?: string
  display_last_name?: string
  display_email?: string
  display_phone?: string
  brokerage_firm_name?: string
  is_trainee?: boolean
  created_at: string
  updated_at: string
}

const UserProfile: React.FC = () => {
  const { t } = useTranslation()
  const { user, registerPasskey, getPasskeys, deletePasskey, supportsPasskeys } = useAuth()
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'account' | 'public' | 'password' | 'security'>('account')
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)
  
  // Quick login preference state
  const [quickLoginPreference, setQuickLoginPreference] = useState<'passkey' | 'sms_2fa' | 'totp_2fa' | null>(null)
  const [availableQuickLoginMethods, setAvailableQuickLoginMethods] = useState({
    passkey: false,
    totp_2fa: false,
    sms_2fa: false
  })
  const [loadingQuickLogin, setLoadingQuickLogin] = useState(false)

  // Profile form state (account information)
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  })

  // Public information form state
  const [publicForm, setPublicForm] = useState({
    display_first_name: '',
    display_last_name: '',
    display_email: '',
    display_phone: '',
    brokerage_firm_name: '',
    is_trainee: false
  })

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })

  // Passkey state
  const [passkeys, setPasskeys] = useState<any[]>([])
  const [loadingPasskeys, setLoadingPasskeys] = useState(false)
  const [registeringPasskey, setRegisteringPasskey] = useState(false)
  const [newPasskeyName, setNewPasskeyName] = useState('')

  // 2FA state
  const [tfaEnabled, setTfaEnabled] = useState(false)
  const [tfaSecret, setTfaSecret] = useState('')
  const [tfaQRCode, setTfaQRCode] = useState('')
  const [tfaToken, setTfaToken] = useState('')
  const [tfaSetupStep, setTfaSetupStep] = useState<'idle' | 'setup' | 'verify'>('idle')
  const [tfaBackupCodes, setTfaBackupCodes] = useState<string[]>([])
  const [loadingTfa, setLoadingTfa] = useState(false)

  // SMS 2FA state
  const [sms2faEnabled, setSms2faEnabled] = useState(false)
  const [sms2faPhone, setSms2faPhone] = useState<string | null>(null)
  const [sms2faPhoneNumber, setSms2faPhoneNumber] = useState('')
  const [sms2faVerificationCode, setSms2faVerificationCode] = useState('')
  const [sms2faSetupStep, setSms2faSetupStep] = useState<'idle' | 'phone' | 'verify'>('idle')
  const [loadingSms2fa, setLoadingSms2fa] = useState(false)
  const [sms2faResendTimer, setSms2faResendTimer] = useState(0)

  // Profile picture state
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null)
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null)
  const [uploadingPicture, setUploadingPicture] = useState(false)
  
  // Photo cropper state
  const [showCropper, setShowCropper] = useState(false)
  const [cropperImage, setCropperImage] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [imageCropSettings, setImageCropSettings] = useState<{crop: {x: number, y: number}, zoom: number, croppedArea: Area} | null>(null)

  useEffect(() => {
    loadUserProfile()
  }, [])

  useEffect(() => {
    if (userProfile) {
      setProfileForm({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        email: userProfile.email || '',
        phone: userProfile.phone || ''
      })
      
      setPublicForm({
        display_first_name: userProfile.display_first_name || '',
        display_last_name: userProfile.display_last_name || '',
        display_email: userProfile.display_email || '',
        display_phone: userProfile.display_phone || '',
        brokerage_firm_name: userProfile.brokerage_firm_name || '',
        is_trainee: userProfile.is_trainee || false
      })
    }
  }, [userProfile])

  // Load passkeys, 2FA, and SMS 2FA status when security tab is activated
  useEffect(() => {
    if (activeTab === 'security') {
      const loadSecurityFeatures = async () => {
        if (supportsPasskeys) {
          await fetchPasskeys()
        }
        await fetchTfaStatus()
        await fetchSms2faStatus()
      }
      loadSecurityFeatures()
    }
  }, [activeTab, supportsPasskeys, getPasskeys])

  // Update available quick login methods when security features change
  useEffect(() => {
    if (activeTab === 'security') {
      setAvailableQuickLoginMethods({
        passkey: passkeys.length > 0,
        totp_2fa: tfaEnabled,
        sms_2fa: sms2faEnabled
      })
      
      // Fetch user's preference from API
      fetchQuickLoginPreference()
    }
  }, [activeTab, passkeys, tfaEnabled, sms2faEnabled])

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  // Crop complete callback
  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  // Helper to load image
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.src = url
    })

  // Create cropped image
  const createCroppedImage = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('No 2d context')
    }

    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Canvas is empty'))
        }
      }, 'image/jpeg', 0.95)
    })
  }

  // Apply crop and close modal
  const applyCrop = async () => {
    if (!cropperImage || !croppedAreaPixels) return

    try {
      const croppedBlob = await createCroppedImage(cropperImage, croppedAreaPixels)
      const croppedFile = new File([croppedBlob], 'profile.jpg', { type: 'image/jpeg' })
      
      setProfilePictureFile(croppedFile)
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result as string)
      }
      reader.readAsDataURL(croppedFile)
      
      // Save crop settings
      setImageCropSettings({
        crop,
        zoom,
        croppedArea: croppedAreaPixels
      })
      
      setShowCropper(false)
      setCropperImage(null)
    } catch (error) {
      console.error('Error cropping image:', error)
      showMessage('error', 'Failed to crop image')
    }
  }

  const loadUserProfile = async () => {
    try {
      const response = await makeApiCall('/api/users/profile')

      if (response.ok) {
        const data = await response.json()
        setUserProfile(data.user)
      } else {
        console.error('Failed to load user profile')
        showMessage('error', t('userProfile.profileInformation.loadError'))
      }
    } catch (error) {
      console.error('Error loading user profile:', error)
      showMessage('error', t('userProfile.profileInformation.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    try {
      const response = await makeJsonApiCall('/api/users/profile', 'PUT', profileForm)
      const data = await response.json()

      if (response.ok) {
        setUserProfile(data.user)
        showMessage('success', t('userProfile.profileInformation.updateSuccess'))
      } else {
        showMessage('error', data.message || t('userProfile.profileInformation.updateError'))
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      showMessage('error', t('userProfile.profileInformation.updateError'))
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showMessage('error', t('userProfile.changePassword.passwordMismatch'))
      return
    }

    setSaving(true)

    try {
      const response = await makeJsonApiCall('/api/users/password', 'PUT', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
        confirm_password: passwordForm.confirm_password
      })

      const data = await response.json()

      if (response.ok) {
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        })
        showMessage('success', t('userProfile.changePassword.changeSuccess'))
      } else {
        showMessage('error', data.message || t('userProfile.changePassword.changeError'))
      }
    } catch (error) {
      console.error('Error changing password:', error)
      showMessage('error', t('userProfile.changePassword.changeError'))
    } finally {
      setSaving(false)
    }
  }

  // Profile picture functions
  const handleProfilePictureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        showMessage('error', t('userProfile.accountInformation.profilePicture.tooLarge'))
        return
      }

      if (!file.type.startsWith('image/')) {
        showMessage('error', t('userProfile.accountInformation.profilePicture.invalidType'))
        return
      }

      // Read file and show cropper
      const reader = new FileReader()
      reader.onloadend = () => {
        setCropperImage(reader.result as string)
        setShowCropper(true)
        // Reset crop settings
        setCrop({ x: 0, y: 0 })
        setZoom(1)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadProfilePicture = async () => {
    if (!profilePictureFile) return

    try {
      setUploadingPicture(true)
      
      const formData = new FormData()
      formData.append('profilePicture', profilePictureFile)
      
      const token = localStorage.getItem('token')
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/users/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
        })

        if (response.ok) {
        const data = await response.json()
        const profilePictureUrl = data.profile_picture_url
        
        // Update with crop settings if available
        if (imageCropSettings) {
          await makeJsonApiCall('/api/users/profile-picture-settings', 'PUT', {
            profile_picture_crop_settings: imageCropSettings
          })
        }
        
        showMessage('success', t('userProfile.accountInformation.profilePicture.uploadSuccess'))
          setUserProfile(prev => prev ? {
            ...prev, 
            profile_picture_url: profilePictureUrl,
          profile_picture_crop_settings: imageCropSettings
          } : null)
          setProfilePictureFile(null)
          setProfilePicturePreview(null)
        await loadUserProfile() // Reload profile to get updated data
        } else {
        showMessage('error', t('userProfile.accountInformation.profilePicture.uploadError'))
        }
    } catch (error) {
      console.error('Error uploading profile picture:', error)
      showMessage('error', t('userProfile.accountInformation.profilePicture.uploadError'))
    } finally {
      setUploadingPicture(false)
    }
  }

  const removeProfilePicture = async () => {
    if (!confirm(t('userProfile.accountInformation.profilePicture.confirmRemove'))) return

    try {
      setUploadingPicture(true)
      
      const response = await makeApiCall('/api/users/profile-picture', {
        method: 'DELETE'
      })

      if (response.ok) {
        showMessage('success', t('userProfile.accountInformation.profilePicture.removeSuccess'))
        setUserProfile(prev => prev ? {
          ...prev, 
          profile_picture_url: undefined,
          profile_picture_settings: undefined
        } : null)
        setProfilePictureFile(null)
        setProfilePicturePreview(null)
      } else {
        showMessage('error', t('userProfile.accountInformation.profilePicture.removeError'))
      }
    } catch (error) {
      console.error('Error removing profile picture:', error)
      showMessage('error', t('userProfile.accountInformation.profilePicture.removeError'))
    } finally {
      setUploadingPicture(false)
    }
  }

  // Public information submit handler
  const handlePublicInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    try {
      const response = await makeJsonApiCall('/api/users/public-profile', 'PUT', publicForm)
      const data = await response.json()

      if (response.ok) {
        // Reload the full profile to ensure we don't lose any fields
        await loadUserProfile()
        showMessage('success', t('userProfile.publicInformation.updateSuccess'))
      } else {
        showMessage('error', data.message || t('userProfile.publicInformation.updateError'))
      }
    } catch (error) {
      console.error('Error updating public information:', error)
      showMessage('error', t('userProfile.publicInformation.updateError'))
    } finally {
      setSaving(false)
    }
  }

  // Passkey functions
  const fetchPasskeys = async () => {
    try {
      setLoadingPasskeys(true)
      const userPasskeys = await getPasskeys()
      setPasskeys(userPasskeys)
    } catch (error) {
      console.error('Failed to fetch passkeys:', error)
      setMessage({ type: 'error', text: 'Failed to load passkeys. Please try again.' })
    } finally {
      setLoadingPasskeys(false)
    }
  }

  const handleRegisterPasskey = async () => {
    try {
      setRegisteringPasskey(true)
      await registerPasskey(newPasskeyName.trim() || undefined)
      setMessage({ type: 'success', text: 'Passkey registered successfully!' })
      setNewPasskeyName('')
      await fetchPasskeys()
    } catch (error: any) {
      console.error('Failed to register passkey:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to register passkey. Please try again.' })
    } finally {
      setRegisteringPasskey(false)
    }
  }

  const handleDeletePasskey = async (passkeyId: number, deviceName: string) => {
    if (!confirm(`Are you sure you want to delete the passkey "${deviceName}"?`)) return

    try {
      await deletePasskey(passkeyId)
      setMessage({ type: 'success', text: 'Passkey deleted successfully!' })
      await fetchPasskeys()
    } catch (error: any) {
      console.error('Failed to delete passkey:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to delete passkey. Please try again.' })
    }
  }

  // 2FA functions
  const fetchTfaStatus = async () => {
    try {
      setLoadingTfa(true)
      const response = await makeApiCall('/api/2fa/status')
      if (response.ok) {
        const data = await response.json()
        setTfaEnabled(data.enabled)
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error)
    } finally {
      setLoadingTfa(false)
    }
  }

  const handleBegin2FASetup = async () => {
    try {
      setLoadingTfa(true)
      const response = await makeJsonApiCall('/api/2fa/setup/begin', 'POST', {})
      if (response.ok) {
        const data = await response.json()
        setTfaSecret(data.secret)
        setTfaQRCode(data.qrCode)
        setTfaSetupStep('setup')
      } else {
        const error = await response.json()
        showMessage('error', error.error || 'Failed to start 2FA setup')
      }
    } catch (error) {
      console.error('Failed to start 2FA setup:', error)
      showMessage('error', 'Failed to start 2FA setup')
    } finally {
      setLoadingTfa(false)
    }
  }

  const handleVerify2FA = async () => {
    try {
      setLoadingTfa(true)
      const response = await makeJsonApiCall('/api/2fa/setup/verify', 'POST', { token: tfaToken })
      if (response.ok) {
        const data = await response.json()
        setTfaBackupCodes(data.backupCodes)
        setTfaEnabled(true)
        setTfaSetupStep('verify')
        showMessage('success', '2FA enabled successfully!')
      } else {
        const error = await response.json()
        showMessage('error', error.error || 'Invalid token')
      }
    } catch (error) {
      console.error('Failed to verify 2FA:', error)
      showMessage('error', 'Failed to verify token')
    } finally {
      setLoadingTfa(false)
    }
  }

  const handleDisable2FA = async () => {
    const password = prompt('Enter your password to disable 2FA:')
    if (!password) return

    try {
      setLoadingTfa(true)
      const response = await makeJsonApiCall('/api/2fa/disable', 'POST', { password })
      if (response.ok) {
        setTfaEnabled(false)
        setTfaSetupStep('idle')
        setTfaSecret('')
        setTfaQRCode('')
        setTfaToken('')
        setTfaBackupCodes([])
        showMessage('success', '2FA disabled successfully')
      } else {
        const error = await response.json()
        showMessage('error', error.error || 'Failed to disable 2FA')
      }
    } catch (error) {
      console.error('Failed to disable 2FA:', error)
      showMessage('error', 'Failed to disable 2FA')
    } finally {
      setLoadingTfa(false)
    }
  }

  const handle2FAComplete = () => {
    setTfaSetupStep('idle')
    setTfaSecret('')
    setTfaQRCode('')
    setTfaToken('')
    setTfaBackupCodes([])
  }

  // SMS 2FA functions
  const fetchSms2faStatus = async () => {
    try {
      setLoadingSms2fa(true)
      const response = await makeApiCall('/api/sms2fa/status')
      if (response.ok) {
        const data = await response.json()
        setSms2faEnabled(data.enabled)
        setSms2faPhone(data.phone)
      }
    } catch (error) {
      console.error('Failed to fetch SMS 2FA status:', error)
    } finally {
      setLoadingSms2fa(false)
    }
  }

  const handleSms2faSendCode = async () => {
    // Start setup flow
    if (sms2faSetupStep === 'idle') {
      setSms2faSetupStep('phone')
      return
    }

    // Validate phone number
    const phoneRegex = /^\+?[1-9]\d{1,14}$/
    if (!phoneRegex.test(sms2faPhoneNumber.replace(/\s/g, ''))) {
      showMessage('error', 'Invalid phone number format. Use E.164 format (e.g., +14165551234)')
      return
    }

    try {
      setLoadingSms2fa(true)
      const response = await makeJsonApiCall('/api/sms2fa/setup/send-code', 'POST', { 
        phone: sms2faPhoneNumber.replace(/\s/g, '') 
      })
      if (response.ok) {
        setSms2faSetupStep('verify')
        showMessage('success', 'Verification code sent to your phone!')
        // Start resend timer
        setSms2faResendTimer(60)
        const interval = setInterval(() => {
          setSms2faResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval)
              return 0
            }
            return prev - 1
          })
        }, 1000)
      } else {
        const error = await response.json()
        showMessage('error', error.error || 'Failed to send verification code')
      }
    } catch (error) {
      console.error('Failed to send SMS code:', error)
      showMessage('error', 'Failed to send verification code')
    } finally {
      setLoadingSms2fa(false)
    }
  }

  const handleSms2faVerify = async () => {
    try {
      setLoadingSms2fa(true)
      const response = await makeJsonApiCall('/api/sms2fa/setup/verify', 'POST', { 
        code: sms2faVerificationCode 
      })
      if (response.ok) {
        setSms2faEnabled(true)
        setSms2faPhone(sms2faPhoneNumber)
        setSms2faSetupStep('idle')
        setSms2faPhoneNumber('')
        setSms2faVerificationCode('')
        showMessage('success', 'SMS 2FA enabled successfully!')
      } else {
        const error = await response.json()
        showMessage('error', error.message || error.error || 'Invalid verification code')
      }
    } catch (error) {
      console.error('Failed to verify SMS code:', error)
      showMessage('error', 'Failed to verify code')
    } finally {
      setLoadingSms2fa(false)
    }
  }

  const handleSms2faResend = async () => {
    try {
      showMessage('success', 'Resending verification code...')
      const response = await makeJsonApiCall('/api/sms2fa/setup/send-code', 'POST', { 
        phone: sms2faPhoneNumber.replace(/\s/g, '') 
      })
      if (response.ok) {
        showMessage('success', 'Verification code resent!')
        // Start resend timer
        setSms2faResendTimer(60)
        const interval = setInterval(() => {
          setSms2faResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval)
              return 0
            }
            return prev - 1
          })
        }, 1000)
      } else {
        const error = await response.json()
        showMessage('error', error.message || 'Failed to resend code')
      }
    } catch (error) {
      console.error('Failed to resend SMS code:', error)
      showMessage('error', 'Failed to resend code')
    }
  }

  const handleSms2faDisable = async (password: string) => {
    try {
      setLoadingSms2fa(true)
      const response = await makeJsonApiCall('/api/sms2fa/disable', 'POST', { password })
      if (response.ok) {
        setSms2faEnabled(false)
        setSms2faPhone(null)
        setSms2faSetupStep('idle')
        setSms2faPhoneNumber('')
        setSms2faVerificationCode('')
        showMessage('success', 'SMS 2FA disabled successfully')
      } else {
        const error = await response.json()
        showMessage('error', error.error || 'Failed to disable SMS 2FA')
      }
    } catch (error) {
      console.error('Failed to disable SMS 2FA:', error)
      showMessage('error', 'Failed to disable SMS 2FA')
    } finally {
      setLoadingSms2fa(false)
    }
  }

  const handleSms2faCancel = () => {
    setSms2faSetupStep('idle')
    setSms2faPhoneNumber('')
    setSms2faVerificationCode('')
  }

  // Quick login preference functions
  const fetchQuickLoginPreference = async () => {
    try {
      setLoadingQuickLogin(true)
      
      // Fetch preference from API
      const response = await makeApiCall('/api/auth/quick-login-preference')
      if (response.ok) {
        const data = await response.json()
        setQuickLoginPreference(data.preferredMethod)
      }
    } catch (error) {
      // API not available yet or error fetching, that's okay
      console.log('Quick login preference API not available:', error)
    } finally {
      setLoadingQuickLogin(false)
    }
  }

  const handleQuickLoginPreferenceChange = async (method: 'passkey' | 'sms_2fa' | 'totp_2fa' | null) => {
    if (method === null) {
      // User wants to disable quick login (no preference)
      setQuickLoginPreference(null)
      showMessage('success', t('userProfile.securityFeatures.quickLogin.preferenceCleared'))
      return
    }

    try {
      setLoadingQuickLogin(true)
      
      // Try to save to API (gracefully fail if not available)
      try {
        const response = await makeJsonApiCall('/api/auth/quick-login-preference', 'POST', {
          preferredMethod: method
        })
        
        if (response.ok) {
          const data = await response.json()
          setQuickLoginPreference(data.preferredMethod)
          showMessage('success', t('userProfile.securityFeatures.quickLogin.preferenceUpdated'))
        } else {
          const error = await response.json()
          showMessage('error', error.message || t('userProfile.securityFeatures.quickLogin.preferenceError'))
        }
      } catch (apiError) {
        // API not available yet - set locally for now
        console.log('Quick login API not available, setting locally')
        setQuickLoginPreference(method)
        showMessage('success', t('userProfile.securityFeatures.quickLogin.preferenceSetLocally'))
      }
    } catch (error) {
      console.error('Failed to update quick login preference:', error)
      showMessage('error', t('userProfile.securityFeatures.quickLogin.preferenceError'))
    } finally {
      setLoadingQuickLogin(false)
    }
  }

  return (
    <SettingsPageTemplate
      title={t('userProfile.title')}
      description={t('userProfile.description')}
      message={message}
      loading={loading}
    >

      {/* Tab Navigation */}
      <div className="mb-8">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('account')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'account'
                ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {t('userProfile.tabs.accountInformation')}
          </button>
          <button
            onClick={() => setActiveTab('public')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'public'
                ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {t('userProfile.tabs.publicInformation')}
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'password'
                ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {t('userProfile.tabs.changePassword')}
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {t('userProfile.tabs.securityFeatures')}
          </button>
        </nav>
      </div>

      {/* Account Information Tab */}
      {activeTab === 'account' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('userProfile.accountInformation.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t('userProfile.accountInformation.description')}
          </p>

          {/* Profile Picture Section */}
          <div className="mb-8 p-6 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('userProfile.accountInformation.profilePicture.title')}</h3>
            
            <div className="flex items-start space-x-6">
              {/* Current/Preview Picture */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 border-4 border-white dark:border-gray-800 shadow-lg">
                  {profilePicturePreview || userProfile?.profile_picture_url ? (
                    <img
                      src={profilePicturePreview || userProfile?.profile_picture_url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-gray-800 to-indigo-600">
                      <span className="text-white text-xl font-bold">
                        {userProfile?.first_name?.charAt(0) || ''}{userProfile?.last_name?.charAt(0) || ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Controls */}
              <div className="flex-1">
                <div className="space-y-4">
                  {/* File Input */}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureSelect}
                      className="hidden"
                      id="profile-picture-upload"
                    />
                    <label
                      htmlFor="profile-picture-upload"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {t('userProfile.accountInformation.profilePicture.choosePicture')}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('userProfile.accountInformation.profilePicture.maxSize')}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    {profilePictureFile && (
                      <button
                        type="button"
                        onClick={uploadProfilePicture}
                        disabled={uploadingPicture}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                      >
                        {uploadingPicture ? t('userProfile.accountInformation.profilePicture.uploading') : t('userProfile.accountInformation.profilePicture.savePicture')}
                      </button>
                    )}
                    
                    {userProfile?.profile_picture_url && (
                      <button
                        type="button"
                        onClick={removeProfilePicture}
                        disabled={uploadingPicture}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        {t('userProfile.accountInformation.profilePicture.removePicture')}
                      </button>
                    )}

                    {profilePictureFile && (
                      <button
                        type="button"
                        onClick={() => {
                          setProfilePictureFile(null)
                          setProfilePicturePreview(null)
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {t('userProfile.accountInformation.profilePicture.cancel')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.accountInformation.fields.firstName')}
                </label>
                <input
                  type="text"
                  id="first_name"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.accountInformation.fields.lastName')}
                </label>
                <input
                  type="text"
                  id="last_name"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.accountInformation.fields.emailAddress')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.accountInformation.fields.phoneNumber')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
              >
                {saving ? t('userProfile.accountInformation.saving') : t('userProfile.accountInformation.saveChanges')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Public Information Tab */}
      {activeTab === 'public' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('userProfile.publicInformation.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t('userProfile.publicInformation.description')}
          </p>

          <form onSubmit={handlePublicInfoSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="display_first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.publicInformation.fields.displayFirstName')}
                </label>
                <input
                  type="text"
                  id="display_first_name"
                  value={publicForm.display_first_name}
                  onChange={(e) => setPublicForm({...publicForm, display_first_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div>
                <label htmlFor="display_last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.publicInformation.fields.displayLastName')}
                </label>
                <input
                  type="text"
                  id="display_last_name"
                  value={publicForm.display_last_name}
                  onChange={(e) => setPublicForm({...publicForm, display_last_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div>
                <label htmlFor="display_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.publicInformation.fields.displayEmail')}
                </label>
                <input
                  type="email"
                  id="display_email"
                  value={publicForm.display_email}
                  onChange={(e) => setPublicForm({...publicForm, display_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div>
                <label htmlFor="display_phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.publicInformation.fields.displayPhone')}
                </label>
                <input
                  type="tel"
                  id="display_phone"
                  value={publicForm.display_phone}
                  onChange={(e) => setPublicForm({...publicForm, display_phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="brokerage_firm_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('userProfile.publicInformation.fields.brokerageFirmName')}
                </label>
                <input
                  type="text"
                  id="brokerage_firm_name"
                  value={publicForm.brokerage_firm_name}
                  onChange={(e) => setPublicForm({...publicForm, brokerage_firm_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_trainee"
                    checked={publicForm.is_trainee}
                    onChange={(e) => setPublicForm({...publicForm, is_trainee: e.target.checked})}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-2 focus:ring-gray-900"
                  />
                  <label htmlFor="is_trainee" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    {t('userProfile.publicInformation.fields.isTrainee')}
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('userProfile.publicInformation.hint')}
              </p>
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
              >
                {saving ? t('userProfile.publicInformation.saving') : t('userProfile.publicInformation.saveChanges')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Change Password Tab */}
      {activeTab === 'password' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            {t('userProfile.changePassword.title')}
          </h2>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div>
              <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('userProfile.changePassword.currentPassword')}
              </label>
              <input
                type="password"
                id="current_password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                required
              />
            </div>

            <div>
              <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('userProfile.changePassword.newPassword')}
              </label>
              <input
                type="password"
                id="new_password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                required
                minLength={8}
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('userProfile.changePassword.passwordRequirements')}
              </p>
            </div>

            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('userProfile.changePassword.confirmPassword')}
              </label>
              <input
                type="password"
                id="confirm_password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                required
              />
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
              >
                {saving ? t('userProfile.changePassword.changing') : t('userProfile.changePassword.changePassword')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Security Features Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {t('userProfile.securityFeatures.aboutTitle')}
                </h3>
                <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                  {t('userProfile.securityFeatures.aboutDescription')}
                </p>
              </div>
            </div>
          </div>

          {/* Passkeys Card */}
          {supportsPasskeys && (
            <PasskeysCard
              passkeys={passkeys}
              loading={loadingPasskeys}
              registering={registeringPasskey}
              newPasskeyName={newPasskeyName}
              onNameChange={setNewPasskeyName}
              onRegister={handleRegisterPasskey}
              onDelete={handleDeletePasskey}
            />
          )}

          {/* 2FA Card */}
          <TwoFactorCard
            enabled={tfaEnabled}
            loading={loadingTfa}
            setupStep={tfaSetupStep}
            secret={tfaSecret}
            qrCode={tfaQRCode}
            token={tfaToken}
            backupCodes={tfaBackupCodes}
            onTokenChange={setTfaToken}
            onBeginSetup={handleBegin2FASetup}
            onVerify={handleVerify2FA}
            onDisable={handleDisable2FA}
            onComplete={handle2FAComplete}
          />

          {/* SMS 2FA Card */}
          <SMS2FACard
            enabled={sms2faEnabled}
            loading={loadingSms2fa}
            phone={sms2faPhone}
            setupStep={sms2faSetupStep}
            phoneNumber={sms2faPhoneNumber}
            verificationCode={sms2faVerificationCode}
            resendTimer={sms2faResendTimer}
            onPhoneChange={setSms2faPhoneNumber}
            onCodeChange={setSms2faVerificationCode}
            onSendCode={handleSms2faSendCode}
            onVerify={handleSms2faVerify}
            onResend={handleSms2faResend}
            onDisable={handleSms2faDisable}
            onCancel={handleSms2faCancel}
          />

          {/* Quick Login Preference Card */}
          {(availableQuickLoginMethods.passkey || availableQuickLoginMethods.totp_2fa || availableQuickLoginMethods.sms_2fa) && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('userProfile.securityFeatures.quickLogin.title')}
                  </h3>
                  {quickLoginPreference && (
                    <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                      {t('userProfile.securityFeatures.quickLogin.active')}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('userProfile.securityFeatures.quickLogin.description')}
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  {availableQuickLoginMethods.passkey && (
                    <label className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="quickLoginMethod"
                        value="passkey"
                        checked={quickLoginPreference === 'passkey'}
                        onChange={() => handleQuickLoginPreferenceChange('passkey')}
                        disabled={loadingQuickLogin}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H4v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium text-gray-900 dark:text-white">{t('userProfile.securityFeatures.quickLogin.passkey')}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('userProfile.securityFeatures.quickLogin.passkeyDescription')}
                        </p>
                      </div>
                    </label>
                  )}

                  {availableQuickLoginMethods.totp_2fa && (
                    <label className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="quickLoginMethod"
                        value="totp_2fa"
                        checked={quickLoginPreference === 'totp_2fa'}
                        onChange={() => handleQuickLoginPreferenceChange('totp_2fa')}
                        disabled={loadingQuickLogin}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium text-gray-900 dark:text-white">{t('userProfile.securityFeatures.quickLogin.authenticatorApp')}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('userProfile.securityFeatures.quickLogin.authenticatorDescription')}
                        </p>
                      </div>
                    </label>
                  )}

                  {availableQuickLoginMethods.sms_2fa && (
                    <label className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="quickLoginMethod"
                        value="sms_2fa"
                        checked={quickLoginPreference === 'sms_2fa'}
                        onChange={() => handleQuickLoginPreferenceChange('sms_2fa')}
                        disabled={loadingQuickLogin}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                          </svg>
                          <span className="font-medium text-gray-900 dark:text-white">{t('userProfile.securityFeatures.quickLogin.smsVerification')}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('userProfile.securityFeatures.quickLogin.smsDescription')}
                        </p>
                      </div>
                    </label>
                  )}
                </div>

                {quickLoginPreference && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {t('userProfile.securityFeatures.quickLogin.noPreferenceNote')}
                    </p>
                  </div>
                )}
              </div>

              {loadingQuickLogin && (
                <div className="mt-4 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          )}

          {!supportsPasskeys && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('userProfile.securityFeatures.passkeysNotSupported.title')}
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                    {t('userProfile.securityFeatures.passkeysNotSupported.message')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Account Details */}
      <div className="mt-8 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('userProfile.accountDetails.title')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">{t('userProfile.accountDetails.accountCreated')}</span>
            <span className="ml-2 text-gray-600 dark:text-gray-400">
              {userProfile?.created_at ? new Date(userProfile.created_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">{t('userProfile.accountDetails.lastUpdated')}</span>
            <span className="ml-2 text-gray-600 dark:text-gray-400">
              {userProfile?.updated_at ? new Date(userProfile.updated_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Photo Cropper Modal */}
      {showCropper && cropperImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('userProfile.accountInformation.profilePicture.cropPhoto')}
              </h3>
            </div>

            {/* Cropper Area */}
            <div className="relative" style={{ height: '400px' }}>
              <Cropper
                image={cropperImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom Control */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1"
                />
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCropper(false)
                  setCropperImage(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('userProfile.accountInformation.profilePicture.cancel')}
              </button>
              <button
                onClick={applyCrop}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
              >
                {t('userProfile.accountInformation.profilePicture.apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </SettingsPageTemplate>
  )
}

export default UserProfile